import React, { useState, useRef } from 'react';
import MailchimpSubscribe from 'react-mailchimp-subscribe';
import { PUBLIC_MAILCHIMP_URL, MAILCHIMP_SIGNUP_HERO_TAG } from '../../utils/constants';
import { validateEmail } from '../../utils/validations';
import {
  Container,
  Header,
  HeaderTitle,
  FormContainer,
  FormGroup,
  Label,
  EmailInput,
  CheckboxGroup,
  Checkbox,
  CheckboxLabel,
  PrivacyLink,
  SubscribeButton,
  Footer,
  FooterLink,
  SuccessMessage,
  SuccessIcon,
  SuccessText
} from './styles';

interface NewsletterFormProps {
  status: string | null;
  message: string | Error | null;
  onValidated: (formData: { EMAIL: string; tags: string }) => void;
}

const NewsletterForm: React.FC<NewsletterFormProps> = ({ status, message, onValidated }) => {
  const [email, setEmail] = useState<string>('');
  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const emailRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setShowError(true);
      return;
    }
    
    if (!validateEmail(email)) {
      setShowError(true);
      return;
    }
    
    if (!agreeTerms) {
      setShowError(true);
      return;
    }

    setShowError(false);
    onValidated({
      EMAIL: email,
      tags: `${MAILCHIMP_SIGNUP_HERO_TAG}`
    });
  };

  React.useEffect(() => {
    if (status === 'success') {
      setShowSuccess(true);
      setEmail('');
      setAgreeTerms(false);
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }
  }, [status]);

  return (
    <Container>
      <Header>
        <HeaderTitle>
          Sign up to receive latest product news, exclusive content and more!
        </HeaderTitle>
      </Header>
      
      <FormContainer>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Your e-mail</Label>
            <EmailInput
              ref={emailRef}
              type="email"
              id="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setShowError(false)}
            />
          </FormGroup>
          
          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="agree-terms"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
            />
            <CheckboxLabel htmlFor="agree-terms">
              By subscribing to our mailing list, you agree to communications from Niyyah in accordance with the terms set out in our{' '}
              <PrivacyLink href="/privacy-policy">
                Privacy Policy
              </PrivacyLink>
              .
            </CheckboxLabel>
          </CheckboxGroup>
          
          <SubscribeButton type="submit">
            Subscribe
          </SubscribeButton>
          
          {showError && (
            <div style={{ color: '#e74c3c', fontSize: '14px', marginTop: '10px' }}>
              Please enter a valid email address and agree to the terms.
            </div>
          )}
          
          {status === 'error' && message && (
            <div style={{ color: '#e74c3c', fontSize: '14px', marginTop: '10px' }}>
              {typeof message === 'string' ? message : message.toString()}
            </div>
          )}
          
          {showSuccess && (
            <SuccessMessage>
              <SuccessIcon>✓</SuccessIcon>
              <SuccessText>Thank you for subscribing to our newsletter!</SuccessText>
            </SuccessMessage>
          )}
        </form>
      </FormContainer>
      
      <Footer>
        <FooterLink href="#" onClick={(e) => e.preventDefault()}>
          Donate
        </FooterLink>
        <FooterLink href="mailto:connect@niyyah.app">
          Contact Us
        </FooterLink>
        <FooterLink href="#" onClick={(e) => e.preventDefault()}>
          Privacy Policy
        </FooterLink>
      </Footer>
    </Container>
  );
};

const NewsletterSignup: React.FC = () => {
  return (
    <MailchimpSubscribe
      url={PUBLIC_MAILCHIMP_URL}
      render={({ subscribe, status, message }) => (
        <NewsletterForm
          status={status}
          message={message}
          onValidated={(formData) => subscribe(formData)}
        />
      )}
    />
  );
};

export { NewsletterSignup };
